import React, { useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import {
  IPreQualifyingQuestionsProps,
  IBankQuestionOption,
} from '../interfaces';
import { usePreQualifyingQuestions, useQuestionBankSearch } from '../Hooks';
import {
  PreQualHeader,
  BankImportSection,
  CreateQuestionCard,
  QuestionList,
  PreQualFooter,
} from '../components';
import { QUESTION_TYPES } from '../constants';
import { isDuplicateQuestion } from '../functions';

export const PreQualifyingQuestionsContainer = ({
  setActiveTab,
  hasCCALicense = false,
}: IPreQualifyingQuestionsProps) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(NS.adminPages);

  const {
    questions,
    isLoading,
    newQuestionText,
    setNewQuestionText,
    newQuestionType,
    setNewQuestionType,
    choices,
    setChoices,
    editIndex,
    setEditIndex,
    editQuestionText,
    setEditQuestionText,
    editQuestionType,
    setEditQuestionType,
    editChoices,
    setEditChoices,
    handleChoiceChange,
    handleAddQuestion,
    handleEditQuestion,
    handleSaveQuestion,
    handleDeleteQuestion,
    handleAddFromBank,
  } = usePreQualifyingQuestions(id || '');

  const { loadOptionsDebounced } = useQuestionBankSearch();

  // Translated question type options
  const questionTypeOptions: SelectOptions[] = useMemo(
    () =>
      QUESTION_TYPES.map((type) => ({
        value: type.value,
        label: t(type.labelKey) || type.defaultLabel,
      })),
    [t],
  );

  // Bank question selection handler with duplicate prevention
  const handleBankQuestionChange = useCallback(
    (selectedOption: IBankQuestionOption) => {
      const bankQuestion = selectedOption?.raw;
      if (!bankQuestion) return;

      if (isDuplicateQuestion(questions, bankQuestion.question)) {
        toast.error(
          t(
            ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying
              .duplicateQuestion,
          ),
          { position: 'bottom-center' },
        );
        return;
      }

      handleAddFromBank({
        question: bankQuestion.question,
        questionType: bankQuestion.questionType,
        choices: bankQuestion.choices || [],
      });
    },
    [questions, handleAddFromBank, t],
  );

  const handleNext = useCallback(() => {
    setActiveTab(hasCCALicense ? 'cca' : 'preview');
  }, [hasCCALicense, setActiveTab]);

  const handleBack = useCallback(() => {
    setActiveTab('videoMessageDetails');
  }, [setActiveTab]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '16px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'slate.200',
        display: 'flex',
        flexDirection: 'column',
        gap: 3.5,
      }}
    >
      {/* Header */}
      <PreQualHeader />

      {/* Bank Import Section */}
      <BankImportSection
        onQuestionChange={handleBankQuestionChange}
        loadOptions={loadOptionsDebounced}
      />

      {/* Create New Question Card */}
      <CreateQuestionCard
        newQuestionText={newQuestionText}
        setNewQuestionText={setNewQuestionText}
        newQuestionType={newQuestionType}
        setNewQuestionType={setNewQuestionType}
        questionTypeOptions={questionTypeOptions}
        choices={choices}
        setChoices={setChoices}
        onChoiceChange={handleChoiceChange}
        onAddQuestion={handleAddQuestion}
        isEditingOther={editIndex !== null}
      />

      {/* Questions List */}
      <QuestionList
        questions={questions}
        isLoading={isLoading}
        editIndex={editIndex}
        editQuestionText={editQuestionText}
        setEditQuestionText={setEditQuestionText}
        editQuestionType={editQuestionType}
        setEditQuestionType={setEditQuestionType}
        questionTypeOptions={questionTypeOptions}
        editChoices={editChoices}
        setEditChoices={setEditChoices}
        onChoiceChange={handleChoiceChange}
        onSaveQuestion={handleSaveQuestion}
        onCancelEdit={() => setEditIndex(null)}
        onEditQuestion={handleEditQuestion}
        onDeleteQuestion={handleDeleteQuestion}
      />

      {/* Footer Navigation */}
      <PreQualFooter onBack={handleBack} onNext={handleNext} />
    </Paper>
  );
};
